import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Clock, CheckCircle, PlayCircle, BookOpen } from 'lucide-react';
import { api } from '@/lib/api';

export function LearnerDashboard() {
  const queryClient = useQueryClient();

  // Get current user
  const { data: userData } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.getCurrentUser(),
  });

  const currentUser = (userData as any)?.user;
  const userId = currentUser?.id;

  // Get enrollments
  const { data, isLoading } = useQuery({
    queryKey: ['enrollments', userId],
    queryFn: () => api.getEnrollments({ userId }),
    enabled: !!userId,
  });

  // Get all published courses
  const { data: coursesData } = useQuery({
    queryKey: ['published-courses'],
    queryFn: () => api.getCourses({ status: 'published' }),
  });

  const enrollments = ((data as any)?.enrollments || []) as any[];
  const allCourses = ((coursesData as any)?.courses || []) as any[];

  const inProgress = enrollments.filter((e) => e.status === 'in_progress');
  const completed = enrollments.filter((e) => e.status === 'completed');

  // Filter available courses (not enrolled)
  const enrolledCourseIds = new Set(enrollments.map((e) => e.course_id));
  const availableCourses = allCourses.filter((c) => !enrolledCourseIds.has(c.id));

  // Enroll mutation
  const enrollMutation = useMutation({
    mutationFn: (courseId: string) => api.enrollUser(userId, courseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
    },
  });

  const handleEnroll = async (courseId: string, courseTitle: string) => {
    if (confirm(`Enroll in "${courseTitle}"?`)) {
      try {
        await enrollMutation.mutateAsync(courseId);
        alert('Successfully enrolled!');
      } catch (error) {
        alert('Failed to enroll. Please try again.');
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Learning</h1>
        <p className="text-gray-600 mt-1">
          Continue your learning journey
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{inProgress.length}</div>
              <div className="text-gray-600 text-sm">In Progress</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{completed.length}</div>
              <div className="text-gray-600 text-sm">Completed</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <PlayCircle className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{enrollments.length}</div>
              <div className="text-gray-600 text-sm">Total Courses</div>
            </div>
          </div>
        </div>
      </div>

      {/* In Progress Courses */}
      {inProgress.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Continue Learning</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {inProgress.map((enrollment) => (
              <Link
                key={enrollment.id}
                to={`/learn/courses/${enrollment.course_id}`}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="h-48 bg-gradient-to-br from-primary-400 to-primary-600"></div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-2">
                    {enrollment.course_title}
                  </h3>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>{enrollment.progress_percentage}% complete</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all"
                        style={{ width: `${enrollment.progress_percentage}%` }}
                      ></div>
                    </div>
                  </div>

                  <button className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                    Continue
                  </button>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* My Enrolled Courses */}
      {enrollments.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">My Courses</h2>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {enrollments.map((enrollment) => (
              <Link
                key={enrollment.id}
                to={`/learn/courses/${enrollment.course_id}`}
                className="flex items-center gap-4 p-4 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-lg flex-shrink-0"></div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg truncate">
                    {enrollment.course_title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {enrollment.status === 'completed'
                      ? 'Completed'
                      : `${enrollment.progress_percentage}% complete`}
                  </p>
                </div>
                {enrollment.status === 'completed' && (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Available Courses to Enroll */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Available Courses</h2>
        {isLoading ? (
          <div className="text-center py-12">Loading...</div>
        ) : availableCourses.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">
              {enrollments.length === 0
                ? 'No courses available yet. Check back soon!'
                : 'You\'re enrolled in all available courses!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableCourses.map((course) => (
              <div
                key={course.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="h-48 bg-gradient-to-br from-blue-400 to-blue-600"></div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-2">{course.title}</h3>
                  <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                    {course.description || 'No description available'}
                  </p>
                  <button
                    onClick={() => handleEnroll(course.id, course.title)}
                    disabled={enrollMutation.isPending}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {enrollMutation.isPending ? 'Enrolling...' : 'Enroll Now'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
